import { useEffect, useRef } from 'react'
import './_liveLeaderboard.scss'

import { useTranslation } from 'react-i18next'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { Crown } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { type EndStreamStats } from 'features/LiveStream/LiveStreamCreator/LiveStreamCreator'
import ViewersList from 'features/LiveStream/ViewersList/ViewersList'


type LiveLeaderboardProps = {
  stats: EndStreamStats | null
  onClose: () => void | Promise<void>
}

const LiveLeaderboard = ({ stats, onClose }: LiveLeaderboardProps) => {
  const viewersRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  const updateMask = () => {
    if (!viewersRef.current) return

    const container = viewersRef.current
    const { scrollHeight, scrollTop, clientHeight } = container
    const remainingArea = scrollHeight - (scrollTop + clientHeight)
    const isScrollable = scrollHeight > clientHeight + 1
    const thresholdReached = remainingArea <= 20

    if (!isScrollable || thresholdReached) {
      container.style.maskImage = 'none'
      container.style.webkitMaskImage = 'none'
      return
    }

    const mask = 'linear-gradient(to top, transparent 5%, black 22%)'
    container.style.maskImage = mask
    container.style.webkitMaskImage = mask
  }

  useEffect(() => {
    updateMask()

    const container = viewersRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      updateMask()
    })

    resizeObserver.observe(container)
    window.addEventListener('resize', updateMask)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateMask)
    }
  }, [stats])

  const tippers = stats?.top_tippers ?? []
  const first = tippers[0]
  const second = tippers[1]
  const third = tippers[2]

const viewers = tippers.map(tipper => ({
  id: tipper.id,
  avatarUrl: tipper.avatar ?? placeholderAvatar,
  name: tipper.name ?? tipper.username ?? `User ${tipper.id}`,
  handle: tipper.username ? `@${tipper.username}` : '',
  tipped: `$${tipper.tip_amount}`,
}))

  return (
    <div className='leaderboard'>
      <div className='leaderboard__toprated'>
        <div className='leaderboard__toprated--item second'>
          {second && (
            <div className='tipper'>
              <div className='tipper__rating'>2</div>
              <div className='tipper__avatar'>
                <img
                  src={second.avatar ?? placeholderAvatar}
                  alt={second.username ?? second.name ?? 'Second place tipper'}
                  onError={e => {
                    e.currentTarget.src = placeholderAvatar
                  }}
                />
              </div>
              <div className='tipper__handle'>
                {second.username ? `@${second.username}` : second.name}
              </div>
              <div className='tipper__amount'>
                <span>$</span>
                {second.tip_amount}
              </div>
            </div>
          )}
        </div>

        <div className='leaderboard__toprated--item first'>
          {first && (
            <div className='tipper'>
              <div className='tipper__avatar'>
                <div className='tipper__avatar--crown'>
                  <Crown />
                </div>
                <img
                  src={first.avatar ?? placeholderAvatar}
                  alt={first.username ?? first.name ?? 'First place tipper'}
                  onError={e => {
                    e.currentTarget.src = placeholderAvatar
                  }}
                />
              </div>
              <div className='tipper__handle'>
                {first.username ? `@${first.username}` : first.name}
              </div>
              <div className='tipper__amount'>
                <span>$</span>
                {first.tip_amount}
              </div>
            </div>
          )}
        </div>

        <div className='leaderboard__toprated--item third'>
          {third && (
            <div className='tipper'>
              <div className='tipper__rating'>3</div>
              <div className='tipper__avatar'>
                <img
                  src={third.avatar ?? placeholderAvatar}
                  alt={third.username ?? third.name ?? 'Third place tipper'}
                  onError={e => {
                    e.currentTarget.src = placeholderAvatar
                  }}
                />
              </div>
              <div className='tipper__handle'>
                {third.username ? `@${third.username}` : third.name}
              </div>
              <div className='tipper__amount'>
                <span>$</span>
                {third.tip_amount}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        ref={viewersRef}
        onScroll={updateMask}
        className='leaderboard__viewers'
      >
        <ViewersList viewers={viewers} type='leaderboard' />
      </div>

      <div className='leaderboard__footer'>
        <Button
          text={t('ok')}
          color='transparent'
          font='mont-14-normal'
          type='transparent--white'
          width='16'
          height='5'
          padding='15'
          clickFn={onClose}
        />
      </div>
    </div>
  )
}

export default LiveLeaderboard